import React, { useState, useEffect, useContext, createContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { THEMES, COLORS, STATUS_COLOR, STATUS_BG, buildStyles, injectDashStyles } from './DashTheme';
import NotificationBell from '../Notifications/NotificationBell';

// ─── Theme Context ────────────────────────────────────────────────────────────
export const ThemeCtx = createContext({ theme: THEMES.dark, toggle: () => {} });

export function ThemeProvider({ children }) {
  const theme = THEMES.dark;
  injectDashStyles();
  return <ThemeCtx.Provider value={{ theme, toggle: () => {} }}>{children}</ThemeCtx.Provider>;
}

export function useTheme() { return useContext(ThemeCtx); }

// ─── Stat Card ────────────────────────────────────────────────────────────────
export function useCountUp(target, duration = 1000) {
  const [count, setCount] = useState(0);
  const prev = React.useRef(0);
  useEffect(() => {
    const start = prev.current;
    const end = Number(target) || 0;
    prev.current = end;
    if (start === end) { setCount(end); return; }
    const steps = 40;
    const stepTime = duration / steps;
    let current = start;
    const inc = (end - start) / steps;
    const timer = setInterval(() => {
      current += inc;
      if ((inc > 0 && current >= end) || (inc < 0 && current <= end)) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.round(current));
      }
    }, stepTime);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
}

export function StatCard({ label, value, color, delay = '0s', onClick, sublabel }) {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const [hov, setHov] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = React.useRef(null);
  const num = Number(value) || 0;
  const displayed = useCountUp(visible ? num : 0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="ur-card-hover"
      style={{
        ...s.statCard,
        cursor: onClick ? 'pointer' : 'default',
        borderColor: hov ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.22)',
        background: hov ? 'rgba(255,255,255,0.18)' : s.statCard.background,
        animationDelay: delay,
        opacity: 0,
        animation: `fadeUp 0.5s ease-out ${delay} both`,
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={onClick}
    >
      <div style={{ ...s.statAccent, background: `linear-gradient(90deg,${color},${color}88)` }} />
      <div style={{
        ...s.statIconWrap, background: color + '25',
        boxShadow: hov ? `0 0 18px ${color}55` : 'none',
        transition: 'box-shadow 0.3s ease',
      }}>
        <div style={{
          width: 10, height: 10, borderRadius: '50%', background: color,
          boxShadow: `0 0 ${hov ? 16 : 8}px ${color}`,
          transition: 'box-shadow 0.3s ease',
        }} />
      </div>
      <div style={{
        ...s.statVal,
        background: `linear-gradient(135deg, #fff 60%, ${color})`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      }}>
        {displayed}
      </div>
      <div style={s.statLabel}>{label}</div>
      {sublabel && <div style={{ ...s.statTrend, color: 'rgba(255,255,255,0.55)' }}>{sublabel}</div>}
    </div>
  );
}

// ─── Animated Inline Stat Card (used by Admin/Authority/HA dashboards) ────────
export function AnimatedStatCard({ label, value, color, onClick, delay = '0s', compact = false }) {
  const [visible, setVisible] = useState(false);
  const ref = React.useRef(null);
  const displayed = useCountUp(visible ? (Number(value) || 0) : 0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const dot = (
    <div style={{
      width: 46, height: 46, borderRadius: 12, flexShrink: 0,
      background: color + '25', border: `1px solid ${color}40`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: compact ? 0 : 14,
    }}>
      <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, boxShadow: `0 0 10px ${color}` }} />
    </div>
  );

  if (compact) {
    return (
      <div ref={ref} onClick={onClick} className={onClick ? 'ur-card-hover' : ''}
        style={{
          borderRadius: 14, padding: '14px 16px', position: 'relative', overflow: 'hidden',
          background: `linear-gradient(135deg, ${color}15, ${color}06)`,
          border: `1px solid ${color}30`,
          display: 'flex', alignItems: 'center', gap: 12,
          cursor: onClick ? 'pointer' : 'default',
          opacity: 0, animation: `fadeUp 0.5s ease-out ${delay} both`,
        }}>
        {dot}
        <div>
          <div style={{
            fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1,
            background: `linear-gradient(135deg, #fff 60%, ${color})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>{displayed}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4, fontWeight: 500 }}>{label}</div>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} onClick={onClick} className={onClick ? 'ur-card-hover' : ''}
      style={{
        borderRadius: 14, padding: '16px 18px', position: 'relative', overflow: 'hidden',
        background: `linear-gradient(135deg, ${color}18, ${color}08)`,
        border: `1px solid ${color}35`,
        boxShadow: `0 4px 20px ${color}15`,
        cursor: onClick ? 'pointer' : 'default',
        opacity: 0, animation: `fadeUp 0.5s ease-out ${delay} both`,
      }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${color}, ${color}55)`, borderRadius: '14px 14px 0 0' }} />
      {dot}
      <div style={{
        fontSize: 30, fontWeight: 800, letterSpacing: '-1px', lineHeight: 1,
        background: `linear-gradient(135deg, #fff 60%, ${color})`,
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
      }}>{displayed}</div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 6, fontWeight: 500 }}>{label}</div>
    </div>
  );
}
export function ProgressBar({ value, color = COLORS.purple, label, showPct = true }) {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const pct = Math.min(Math.max(value, 0), 100);
  return (
    <div style={s.progressWrap}>
      {label && (
        <div style={s.progressLabel}>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>{label}</span>
          {showPct && <span style={{ fontSize: 12, color, fontWeight: 700 }}>{pct}%</span>}
        </div>
      )}
      <div style={s.progressTrack}>
        <div style={{ ...s.progressFill, width: `${pct}%`, background: `linear-gradient(90deg,${color},${color}bb)` }} />
      </div>
    </div>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
export function StatusBadge({ status }) {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const color = STATUS_COLOR[status] || COLORS.purple;
  const bg    = STATUS_BG[status]    || COLORS.purple + '30';
  return <span style={{ ...s.badge, background: bg, color }}>{status}</span>;
}

// ─── Live Dot ─────────────────────────────────────────────────────────────────
export function LiveDot({ label = 'Live' }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, color:'#6ee7b7', fontWeight:600 }}>
      <span style={{ width:7, height:7, borderRadius:'50%', background:'#10b981', display:'inline-block', animation:'livePulse 2s infinite' }} />
      {label}
    </span>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────
export function SectionCard({ title, subtitle, action, children, delay = '0s', style: extra }) {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  return (
    <div className="ur-card-hover" style={{ ...s.card, animationDelay: delay, ...extra }}>
      <div style={s.sectionHead}>
        <div>
          <div style={s.sectionTitle}>{title}</div>
          {subtitle && <div style={s.sectionSub}>{subtitle}</div>}
        </div>
        {action && <div>{action}</div>}
      </div>
      {children}
    </div>
  );
}

// ─── Action Block ─────────────────────────────────────────────────────────────
export function ActionBlock({ label, sub, color, icon, onClick, delay = '0s' }) {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const [hov, setHov] = useState(false);
  return (
    <div
      className="ur-action-block ur-card-hover"
      style={{
        ...s.actionBlock,
        animationDelay: delay,
        borderColor: hov ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)',
        background: hov ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.12)',
        boxShadow: hov ? `0 16px 40px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.3)` : '0 4px 16px rgba(0,0,0,0.1)',
        transform: hov ? 'translateY(-6px) scale(1.03)' : 'translateY(0) scale(1)',
        minHeight: 120,
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={onClick}
    >
      <div style={{
        ...s.actionBlockIcon,
        background: color + '35',
        border: `1px solid ${color}66`,
        width: 56, height: 56, borderRadius: 16,
        fontSize: 24,
        boxShadow: hov ? `0 0 20px ${color}55` : 'none',
        transition: 'all 0.3s ease',
        transform: hov ? 'scale(1.15) rotate(5deg)' : 'scale(1)',
      }}>
        {icon || <div style={{ width: 14, height: 14, borderRadius: '50%', background: color, boxShadow: `0 0 12px ${color}` }} />}
      </div>
      <div style={{ ...s.actionBlockLabel, fontSize: 14 }}>{label}</div>
      {sub && <div style={{ ...s.actionBlockSub, fontSize: 12 }}>{sub}</div>}
    </div>
  );
}

// ─── Complaint Table Row ──────────────────────────────────────────────────────
export function ComplaintRow({ c, onClick, showStudent = false }) {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const overdue = c.is_overdue;
  return (
    <tr className="ur-row-hover" onClick={onClick}>
      <td style={s.tdBold}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {overdue && <span style={{ width:6, height:6, borderRadius:'50%', background:COLORS.red, flexShrink:0, boxShadow:`0 0 6px ${COLORS.red}` }} />}
          <span style={{ color: overdue ? '#fca5a5' : '#fff' }}>{c.title}</span>
        </div>
        {showStudent && c.student_name && <div style={{ fontSize:11, color:'rgba(255,255,255,0.55)', marginTop:2 }}>{c.student_name}</div>}
      </td>
      <td style={s.td}><StatusBadge status={c.status || 'pending'} /></td>
      <td style={s.td}><span style={{ fontSize:12 }}>{c.department || '—'}</span></td>
      <td style={s.td}><span style={{ fontSize:12 }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</span></td>
      {c.verification_deadline !== undefined && (
        <td style={s.td}>
          <span style={{ fontSize:12, color: overdue ? '#fca5a5' : 'rgba(255,255,255,0.8)', fontWeight: overdue ? 700 : 400 }}>
            {c.verification_deadline ? new Date(c.verification_deadline).toLocaleDateString() : '—'}
          </span>
        </td>
      )}
    </tr>
  );
}

// ─── Topbar ───────────────────────────────────────────────────────────────────
export function Topbar({ title, subtitle, actions, onToggleSidebar, onMobileMenu }) {
  const { user } = useAuth();
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  return (
    <div style={{
      ...s.topbar,
      height: 'auto',
      padding: isMobile ? '12px 14px' : '16px 28px',
      flexDirection: 'column',
      gap: 14,
      alignItems: 'stretch',
    }}>
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14, minWidth: 0, flex: 1 }}>
          {/* Mobile hamburger */}
          {isMobile && (
            <div onClick={onMobileMenu || onToggleSidebar} style={{
              width: 36, height: 36, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 4,
              cursor: 'pointer', borderRadius: 9,
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)',
              flexShrink: 0,
            }}>
              <div style={{ width: 16, height: 2, borderRadius: 2, background: '#fff' }} />
              <div style={{ width: 16, height: 2, borderRadius: 2, background: '#fff' }} />
              <div style={{ width: 16, height: 2, borderRadius: 2, background: '#fff' }} />
            </div>
          )}
          {/* Desktop sidebar toggle */}
          {!isMobile && onToggleSidebar && (
            <div onClick={onToggleSidebar} style={{
              width: 40, height: 40, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 5,
              cursor: 'pointer', borderRadius: 10,
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)',
              flexShrink: 0, transition: 'all 0.2s ease',
            }}>
              <div style={{ width: 18, height: 2, borderRadius: 2, background: '#fff' }} />
              <div style={{ width: 18, height: 2, borderRadius: 2, background: '#fff' }} />
              <div style={{ width: 18, height: 2, borderRadius: 2, background: '#fff' }} />
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ ...s.topbarTitle, fontSize: isMobile ? 16 : 20, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
            {subtitle && !isMobile && <div style={s.topbarSub}>{subtitle}</div>}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10, flexShrink: 0 }}>

          {actions}
          <NotificationBell />
          {/* Profile chip — avatar only on mobile */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: isMobile ? '4px' : '8px 16px',
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: 12, cursor: 'pointer',
            backdropFilter: 'blur(8px)',
            transition: 'all 0.2s ease',
          }} onClick={() => navigate('/profile')}>
            <div style={{
              width: isMobile ? 34 : 40, height: isMobile ? 34 : 40, borderRadius: 10,
              background: 'linear-gradient(135deg,rgba(255,255,255,0.3),rgba(255,255,255,0.15))',
              border: '1px solid rgba(255,255,255,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: isMobile ? 14 : 16, fontWeight: 800, color: '#fff', flexShrink: 0,
            }}>
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            {!isMobile && (
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                  {user?.name || 'User'}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 1 }}>
                  {user?.role?.replace('_', ' ') || 'Member'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Page Header Block ────────────────────────────────────────────────────────
export function PageHeader({ icon, title, subtitle, stats, actions }) {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  return (
    <div className="ur-card-hover" style={{
      ...s.card,
      marginBottom: 24,
      padding: '28px 32px',
      background: 'rgba(255,255,255,0.15)',
      border: '1px solid rgba(255,255,255,0.25)',
      animation: 'slideInUp 0.5s ease-out',
      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          {icon && (
            <div style={{
              width: 64, height: 64, borderRadius: 18,
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, flexShrink: 0,
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              animation: 'bounce 3s ease-in-out infinite',
            }}>{icon}</div>
          )}
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#fff', marginBottom: 4, letterSpacing: '-0.3px' }}>{title}</div>
            {subtitle && <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>{subtitle}</div>}
          </div>
        </div>
        {stats && stats.length > 0 && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {stats.map((stat) => {
              const label = Array.isArray(stat) ? stat[0] : stat.label;
              const value = Array.isArray(stat) ? stat[1] : stat.value;
              const color = Array.isArray(stat) ? stat[2] : stat.color;
              return (
                <div key={label} style={{
                  padding: '10px 18px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  textAlign: 'center', minWidth: 80,
                  animation: 'fadeUp 0.5s ease-out both',
                }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: color || '#fff' }}>{value}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{label}</div>
                </div>
              );
            })}
          </div>
        )}
        {actions && <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{actions}</div>}
      </div>
    </div>
  );
}

// ─── Loading Screen ───────────────────────────────────────────────────────────
export function LoadingScreen() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  return (
    <div style={s.loadWrap}>
      <div style={s.spinner} />
      <div style={s.loadText}>Loading...</div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
export function EmptyState({ icon = '—', text = 'Nothing here yet', sub }) {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  return (
    <div style={s.empty}>
      <div style={s.emptyIcon}>{icon}</div>
      <div style={s.emptyText}>{text}</div>
      {sub && <div style={s.emptySub}>{sub}</div>}
    </div>
  );
}

// ─── Deadline Chip ────────────────────────────────────────────────────────────
export function DeadlineChip({ seconds }) {
  if (seconds === undefined || seconds === null) return <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>—</span>;
  const overdue = seconds < 0;
  const abs = Math.abs(seconds);
  const d = Math.floor(abs / 86400);
  const h = Math.floor((abs % 86400) / 3600);
  const label = d > 0 ? `${d}d ${h}h` : `${h}h`;
  const color = overdue ? COLORS.red : seconds < 86400 ? COLORS.amber : COLORS.green;
  return (
    <span style={{ fontSize: 12, color, fontWeight: 700, padding: '2px 8px', background: color + '25', borderRadius: 5 }}>
      {overdue ? `Overdue ${label}` : `${label} left`}
    </span>
  );
}

// ─── User Profile Card ────────────────────────────────────────────────────────
export function UserProfileCard({ user }) {
  const { theme } = useTheme();

  if (!user) return null;

  const roleConfig = {
    student:          { label: 'Student',             color: '#06b6d4', icon: '🎓' },
    faculty:          { label: 'Faculty',              color: '#8b5cf6', icon: '👨‍🏫' },
    admin:            { label: user.admin_type === 'university' ? 'University Admin' : 'Dept Admin', color: '#f59e0b', icon: '🛡️' },
    authority:        { label: user.authority_type === 'university' ? 'University Authority' : 'Dept Authority', color: '#6366f1', icon: '⚖️' },
    higher_authority: { label: 'Higher Authority',    color: '#10b981', icon: '👑' },
  };

  const cfg = roleConfig[user.role] || { label: user.role || 'User', color: '#6366f1', icon: '👤' };
  const initials = (user.name || 'U').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div style={{
      padding: '16px 20px', borderRadius: 14, marginBottom: 20,
      background: `linear-gradient(135deg, ${cfg.color}15, rgba(255,255,255,0.04))`,
      border: `1px solid ${cfg.color}30`,
      animation: 'fadeUp 0.4s ease-out both',
      overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {/* Avatar */}
        <div style={{
          width: 52, height: 52, borderRadius: 14, flexShrink: 0,
          background: `linear-gradient(135deg, ${cfg.color}55, ${cfg.color}22)`,
          border: `2px solid ${cfg.color}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 800, color: '#fff',
          boxShadow: `0 4px 14px ${cfg.color}30`,
        }}>
          {initials}
        </div>

        {/* Name + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name */}
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.name || 'User'}
          </div>
          {/* Role pill + dept on same row, both contained */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
              background: cfg.color + '28', color: cfg.color,
              border: `1px solid ${cfg.color}40`,
              letterSpacing: 0.3, whiteSpace: 'nowrap',
            }}>
              {cfg.icon} {cfg.label}
            </span>
            {user.department && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 11, color: 'rgba(255,255,255,0.5)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180,
              }}>
                🏛️ {user.department}
              </span>
            )}
          </div>
          {/* Email on its own line */}
          {user.email && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.email}
            </div>
          )}
        </div>

        {/* Negligence flag — right side, only if present */}
        {user.negligence_count > 0 && (
          <div style={{
            flexShrink: 0, padding: '5px 10px', borderRadius: 8,
            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)',
            fontSize: 11, color: '#fca5a5', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap',
          }}>
            ⚠️ {user.negligence_count}
          </div>
        )}
      </div>
    </div>
  );
}
